/**
 * Composer `@` mentions.
 *
 * A mention is the student pointing at their own saved work — "this plan", "that
 * schedule" — without having to be standing on its page. It is deliberately
 * separate from page context: page context says what is on screen, a mention says
 * what the student meant. Both reach the server, and the server trusts neither for
 * ownership: every identifier is re-checked against the authenticated owner.
 */

export type AssistantMentionKind = 'schedule' | 'degree_plan' | 'course_list';

export type AssistantMention = {
  kind: AssistantMentionKind;
  id: string;
  label: string;
};

export type AssistantMentionCandidate = AssistantMention & {
  /** Secondary line in the picker: term, semester count, course count. */
  detail?: string;
};

export const ASSISTANT_MENTION_KIND_LABELS: Record<AssistantMentionKind, string> = {
  schedule: 'Schedule',
  degree_plan: 'Degree plan',
  course_list: 'Course list',
};

const MENTION_TOKEN = /(^|\s)@([^\s@]{0,60})$/;

/**
 * The trailing `@token` the caret is sitting in, or null. Anchored to the end of
 * the text so an `@` earlier in a finished sentence does not reopen the picker.
 */
export function assistantMentionQuery(text: string): string | null {
  const match = MENTION_TOKEN.exec(text);
  return match ? match[2].toLowerCase() : null;
}

function matches(candidate: AssistantMentionCandidate, query: string): boolean {
  if (!query) return true;
  const haystack = `${candidate.label} ${candidate.detail ?? ''} ${ASSISTANT_MENTION_KIND_LABELS[candidate.kind]}`.toLowerCase();
  // Every whitespace-separated fragment must appear, so "fall sched" narrows
  // rather than widening the way a single substring test would.
  return query.split(/\s+/).filter(Boolean).every((term) => haystack.includes(term));
}

export function filterAssistantMentions(
  candidates: readonly AssistantMentionCandidate[],
  query: string,
  limit = 8,
): AssistantMentionCandidate[] {
  const scored = candidates.filter((candidate) => matches(candidate, query));
  const prefix = query ? scored.filter((candidate) => candidate.label.toLowerCase().startsWith(query)) : [];
  const rest = scored.filter((candidate) => !prefix.includes(candidate));
  return [...prefix, ...rest].slice(0, limit);
}

/** Replace the trailing `@token` with the candidate's label, leaving a trailing space. */
export function applyAssistantMention(text: string, candidate: AssistantMentionCandidate): string {
  const match = MENTION_TOKEN.exec(text);
  if (!match) return text;
  return `${text.slice(0, match.index)}${match[1]}@${candidate.label} `;
}

/** Keep only mentions whose label still appears in the message being sent. */
export function retainMentionedInText(mentions: readonly AssistantMention[], text: string): AssistantMention[] {
  const haystack = text.toLowerCase();
  const seen = new Set<string>();
  return mentions.filter((mention) => {
    const key = `${mention.kind}:${mention.id}`;
    if (seen.has(key) || !haystack.includes(`@${mention.label.toLowerCase()}`)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * An explicit mention overrides what happens to be on screen, so `get_selected_schedule`
 * and `get_selected_degree_plan` resolve to the thing the student named.
 */
export function mentionSelectionOverrides(mentions: readonly AssistantMention[]): {
  selectedScheduleId?: string;
  selectedDegreePlanId?: string;
} {
  const schedule = mentions.find((mention) => mention.kind === 'schedule');
  const plan = mentions.find((mention) => mention.kind === 'degree_plan');
  return {
    ...(schedule ? { selectedScheduleId: schedule.id } : {}),
    ...(plan ? { selectedDegreePlanId: plan.id } : {}),
  };
}
